import { AxiosResponse } from 'axios';
import {
  BusinessSectorDto,
  CollectiveAgreementDto,
  CompanyDto,
  UserInvitation,
} from '@jaam/commons/dtos';
import { ICompanyUniqueParams } from '@jaam/commons/params';
import { PAYROLLR_API as API } from '@jaam/web-core';

const formatQueryParams = (params: Record<string, any>): string => {
  if (!params || typeof params !== 'object') return '';

  const queryParams = Object.entries(params)
    .filter(([_, value]) => value !== undefined && value !== null) // Exclude null and undefined
    .map(
      ([key, value]) =>
        `${encodeURIComponent(key)}=${encodeURIComponent(value)}`,
    )
    .join('&');

  return queryParams ? `?${queryParams}` : '';
};

const CompanyServicesAPI = {
  /**
   * Update a specific company by ID.
   * @param {number} id - Company ID
   * @param {Partial<CompanyDto>} companyDto - Partial company data for updates
   * @returns {Promise<AxiosResponse<CompanyDto>>}
   */
  update: (
    id: number,
    companyDto: Partial<CompanyDto>,
  ): Promise<AxiosResponse<CompanyDto>> => {
    return API.patch(`/companies/${id}`, companyDto);
  },

  /**
   * Add a new company.
   * @param {CompanyDto} company - Company data for creation
   * @returns {Promise<AxiosResponse<CompanyDto>>}
   */
  add: (company: CompanyDto): Promise<AxiosResponse<CompanyDto>> => {
    return API.post('/companies/', company);
  },

  /**
   * Fetch a list of all companies associated with the user.
   * @returns {Promise<AxiosResponse<CompanyDto[]>>}
   */
  list: (): Promise<AxiosResponse<CompanyDto[]>> => {
    return API.get('/users/companies');
  },

  /**
   * Find one company based on unique parameters.
   * @param {ICompanyUniqueParams} params - Unique search parameters for a company
   * @returns {Promise<AxiosResponse<CompanyDto>>}
   */
  findOne: (
    params: ICompanyUniqueParams,
  ): Promise<AxiosResponse<CompanyDto>> => {
    const url = `/companies/${formatQueryParams(params)}`;
    return API.get(url);
  },

  /**
   * Retrieve details of a specific company by ID.
   * @param {number} id - Company ID
   * @returns {Promise<AxiosResponse<CompanyDto>>}
   */
  getOne: (id: number): Promise<AxiosResponse<CompanyDto>> => {
    return API.get(`/companies/${id}`);
  },

  /**
   * Delete a specific company by ID.
   * @param {number} id - Company ID
   * @returns {Promise<AxiosResponse<void>>}
   */
  delete: (id: number): Promise<AxiosResponse<void>> => {
    return API.delete(`/companies/${id}`);
  },
  /**
   * Invite users to a specific company.
   * @param {number} companyId - ID of the company
   * @param {UserInvitation[]} invitations - List of user invitations
   * @returns {Promise<AxiosResponse<void>>}
   */
  inviteCompany: (
    companyId: number,
    invitations: UserInvitation[],
  ): Promise<AxiosResponse<void>> => {
    return API.post(`/companies/${companyId}/invitations`, { invitations });
  },
  getCollectiveAgreements: (): Promise<
    AxiosResponse<CollectiveAgreementDto[]>
  > => API.get('/collective-agreements'),

  getBusinessSectors: (): Promise<AxiosResponse<BusinessSectorDto[]>> =>
    API.get('/business-sectors'),
};

export default CompanyServicesAPI;
